import type { JSX } from 'react';
import { useEffect, useRef } from 'react';

import { Scale, Wrench } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

import type { AgentEvalRubricName, AgentTestRunItem, RunItemChatMessage } from '../../model/run';
import { RUBRIC_LABELS, SCORE_TONE_CLASSES, formatScore, scoreTone } from '../../model/run';

const rubricValueOf = (item: AgentTestRunItem, rubric: AgentEvalRubricName): number | null => {
  const evaluation = item.evaluations?.find((entry) => entry.name === rubric);
  return typeof evaluation?.value === 'number' ? evaluation.value : null;
};

const ToolBubble = ({ label, content }: { label: string; content: string }): JSX.Element => (
  <div className="flex justify-end">
    <div className="flex max-w-[85%] flex-col gap-1 rounded-lg bg-brand-vivid px-3 py-2 opacity-80">
      <span className="flex items-center gap-1.5 text-[0.7rem] font-medium text-white/80">
        <Wrench className="size-3" aria-hidden />
        {label}
      </span>
      <pre className="m-0 font-mono text-xs break-words whitespace-pre-wrap text-white">{content}</pre>
    </div>
  </div>
);

const ChatBubble = ({ role, content }: { role: 'user' | 'assistant'; content: string }): JSX.Element => {
  const isUser = role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-start' : 'justify-end')}>
      <div
        className={cn(
          'flex max-w-[85%] flex-col gap-0.5 rounded-lg px-3 py-2',
          isUser ? 'bg-bg-surface-alt' : 'bg-brand-vivid'
        )}
      >
        <span className={cn('text-[0.7rem] font-medium', isUser ? 'text-text-muted' : 'text-white/80')}>
          {isUser ? 'Cliente' : 'Agente'}
        </span>
        <p className={cn('m-0 text-sm break-words', isUser ? 'text-text-default' : 'text-white')}>{content}</p>
      </div>
    </div>
  );
};

const ConversationMessages = ({ messages }: { messages: RunItemChatMessage[] }): JSX.Element => {
  const toolNameById = new Map<string, string>();
  for (const message of messages) {
    for (const toolCall of message.tool_calls ?? []) {
      if (toolCall.function?.name) toolNameById.set(toolCall.id, toolCall.function.name);
    }
  }

  return (
    <>
      {messages.map((message, index) => {
        if (message.role === 'user' && typeof message.content === 'string' && message.content.length > 0) {
          return <ChatBubble key={index} role="user" content={message.content} />;
        }
        if (message.role === 'tool') {
          const toolName = message.tool_call_id ? toolNameById.get(message.tool_call_id) : undefined;
          return (
            <ToolBubble
              key={index}
              label={`Tool${toolName ? ` · ${toolName}` : ''}`}
              content={typeof message.content === 'string' ? message.content : ''}
            />
          );
        }
        if (message.role === 'assistant' && typeof message.content === 'string' && message.content.length > 0) {
          return <ChatBubble key={index} role="assistant" content={message.content} />;
        }
        return null;
      })}
    </>
  );
};

interface RunItemDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scenarioName: string;
  scenarioDescription?: string;
  rubric: AgentEvalRubricName;
  item: AgentTestRunItem | null;
}

const RunItemDrawer = ({
  open,
  onOpenChange,
  scenarioName,
  scenarioDescription,
  rubric,
  item,
}: RunItemDrawerProps): JSX.Element | null => {
  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        const el = chatRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, item?.id]);

  if (!item) return null;

  const value = rubricValueOf(item, rubric);
  const comment = item.evaluations?.find((entry) => entry.name === rubric)?.comment;
  const messages = item.messages ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle className="text-[0.95rem]">{scenarioName}</DialogTitle>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-block rounded-full bg-brand-vivid/10 px-2 py-0.5 text-[0.7rem] font-semibold text-brand-vivid">
              {RUBRIC_LABELS[rubric]}
            </span>
            {value !== null ? (
              <span
                className={cn(
                  'inline-block rounded-md px-2 py-0.5 text-[0.7rem] font-semibold',
                  SCORE_TONE_CLASSES[scoreTone(value)].bg,
                  SCORE_TONE_CLASSES[scoreTone(value)].text
                )}
              >
                {formatScore(value)}
              </span>
            ) : null}
            <span className="text-xs text-text-muted">juez en Langfuse</span>
          </div>
          {scenarioDescription ? <p className="m-0 text-xs text-text-muted">{scenarioDescription}</p> : null}
        </DialogHeader>

        <div
          ref={chatRef}
          className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto rounded-lg border border-border-subtle p-3"
        >
          {messages.length > 0 ? (
            <ConversationMessages messages={messages} />
          ) : (
            <>
              {item.toolCalls?.map((toolCall, index) => (
                <ToolBubble
                  key={index}
                  label={`Tool · ${toolCall.name}(${toolCall.arguments})`}
                  content={`→ ${toolCall.response}`}
                />
              ))}
              {item.output ? <ChatBubble role="assistant" content={item.output} /> : null}
              {!item.output && (item.toolCalls?.length ?? 0) === 0 ? (
                <p className="m-0 py-6 text-center text-sm text-text-muted">
                  Esta iteración no tiene conversación registrada.
                </p>
              ) : null}
            </>
          )}
        </div>

        {comment ? (
          <p className="m-0 flex items-start gap-2 rounded-lg bg-bg-surface-alt px-3 py-2 text-sm text-text-default italic">
            <Scale className="mt-0.5 size-4 shrink-0" aria-hidden />
            <span>Juez: {comment}</span>
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default RunItemDrawer;
